// Config contract for the Product Scan step (BR-5571). Field names are frozen by the parallel
// admin task br5571-scan-admin — do not rename; all new fields are optional with the defaults
// below so the step renders against a config that only has today's BR-5570 fields.

export type SplashStepIcon = 'find' | 'scan' | 'spin'

export type SplashStepRow = {
  icon: SplashStepIcon
  title: string
  description: string
}

export type ProductScanProductConfig = {
  upc: string
  productName?: string
  brand?: string
  imageUrl?: string
  category?: string
  description?: string
}

export type ProductScanRawConfig = {
  headline?: string
  introCopy?: string
  headerImageId?: string
  submissionCopy?: string
  validScanHeadline?: string
  validScanBody?: string
  invalidScanHeadline?: string
  invalidScanBody?: string
  products?: ProductScanProductConfig[]

  splashCtaText?: string
  splashSteps?: SplashStepRow[]
  scannerTitle?: string
  scannerHelperText?: string
  scannerInstructions?: string
  invalidScanPillText?: string
  retryCtaText?: string
  successTitle?: string
  successCtaText?: string
  unlockLabel?: string
  unlockValue?: string
  rewardHeadline?: string
  rewardBody?: string
  rewardCtaText?: string
  boxBackgroundColor?: string
  boxTextColor?: string
}

export type ProductScanConfig = {
  headline: string
  introCopy: string
  headerImageId?: string
  submissionCopy: string
  validScanHeadline: string
  validScanBody: string
  invalidScanHeadline: string
  invalidScanBody: string
  products: ProductScanProductConfig[]

  splashCtaText: string
  splashSteps: SplashStepRow[]
  scannerTitle: string
  scannerHelperText: string
  scannerInstructions: string
  invalidScanPillText: string
  retryCtaText: string
  successTitle: string
  successCtaText: string
  unlockLabel: string
  unlockValue?: string
  rewardHeadline?: string
  rewardBody?: string
  rewardCtaText?: string
  boxBackgroundColor: string
  boxTextColor: string
}

// Translated defaults are resolved by the caller (t()) and passed in as `defaults` so this
// module stays i18n-agnostic and unit-testable without a translation provider.
export type ProductScanDefaults = Pick<
  ProductScanConfig,
  | 'splashCtaText'
  | 'scannerTitle'
  | 'scannerHelperText'
  | 'scannerInstructions'
  | 'invalidScanPillText'
  | 'retryCtaText'
  | 'successTitle'
  | 'successCtaText'
  | 'unlockLabel'
>

export const withProductScanDefaults = (
  raw: ProductScanRawConfig | undefined,
  defaults: ProductScanDefaults,
): ProductScanConfig => ({
  headline: raw?.headline || '',
  introCopy: raw?.introCopy || '',
  headerImageId: raw?.headerImageId,
  submissionCopy: raw?.submissionCopy || '',
  validScanHeadline: raw?.validScanHeadline || '',
  validScanBody: raw?.validScanBody || '',
  invalidScanHeadline: raw?.invalidScanHeadline || '',
  invalidScanBody: raw?.invalidScanBody || '',
  products: raw?.products || [],

  splashCtaText: raw?.splashCtaText || defaults.splashCtaText,
  splashSteps: raw?.splashSteps || [],
  scannerTitle: raw?.scannerTitle || defaults.scannerTitle,
  scannerHelperText: raw?.scannerHelperText || defaults.scannerHelperText,
  scannerInstructions: raw?.scannerInstructions || defaults.scannerInstructions,
  invalidScanPillText: raw?.invalidScanPillText || defaults.invalidScanPillText,
  retryCtaText: raw?.retryCtaText || defaults.retryCtaText,
  successTitle: raw?.successTitle || defaults.successTitle,
  successCtaText: raw?.successCtaText || defaults.successCtaText,
  unlockLabel: raw?.unlockLabel || defaults.unlockLabel,
  unlockValue: raw?.unlockValue || undefined,
  rewardHeadline: raw?.rewardHeadline || undefined,
  rewardBody: raw?.rewardBody || undefined,
  rewardCtaText: raw?.rewardCtaText || undefined,
  boxBackgroundColor: raw?.boxBackgroundColor || '#000000',
  boxTextColor: raw?.boxTextColor || '#FFFFFF',
})

// Response only carries {upc, productName, brand, imageUrl}; category/description live on the
// step config and are resolved back by matching the response's (pattern) upc against products[].
export const findProductConfig = (
  products: ProductScanProductConfig[],
  upc: string | undefined,
): ProductScanProductConfig | undefined =>
  upc ? products.find(p => p.upc === upc) : undefined
